import React, { useCallback } from "react";
import ListItem from "../../../ui_atoms/ListItem";
import OvertimePaidCreateModal from "./OvertimePaidCreateModal";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import { Alert } from "../../Alert";
import VStack from "../../../ui_atoms/VStack";
import { TitleMediumText } from "../../FormattedText";
import IconButton from "../../IconButton";
import { formatCurrency } from "../../../utils";
import { StyleSheet, View } from "react-native";
import Text from "../../Text";

type OvertimePaidProps = {
    onCreate: (newWorkDelta: number) => void;
    onClear: () => void;
    disabled?: boolean;
    value?: string;
    overtimeWorkDelta: number;
    hourlyInvoiceAmount: number;
    formattedEndOfMonthOvertimeTotal: number;
};

const OvertimePaid: React.FC<OvertimePaidProps> = ({
    onCreate,
    onClear,
    disabled = false,
    value,
    overtimeWorkDelta,
    hourlyInvoiceAmount,
    formattedEndOfMonthOvertimeTotal,
}) => {
    const { marginSize, colors } = useTheme();
    const [modalVisible, setModalVisible] = React.useState(false);
    const i18n = useI18n();
    const styles = useStyles();

    const onActionRemove = useCallback(() => {
        Alert.alert(
            i18n.t("overtimePaid.remove_title", { count: Number(value) }),
            i18n.t("overtimePaid.remove_info", { count: Number(value) }),
            [
                {
                    text: i18n.t("cancel"),
                    style: "cancel",
                },
                {
                    text: i18n.t("overtimePaid.remove_action"),
                    onPress: onClear,
                    style: "destructive",
                },
            ],
        );
    }, [i18n, onClear, value]);

    return (
        <VStack gap="1/3">
            <View style={styles.titleContainer}>
                <TitleMediumText>{i18n.t("overtimePaid.title")}</TitleMediumText>
                <Text
                    style={[styles.overtimeHours, overtimeWorkDelta < 0 && { color: colors.error }]}
                >
                    {overtimeWorkDelta > 0 ? "+ " : overtimeWorkDelta < 0 ? "- " : ""}
                    {Math.abs(overtimeWorkDelta)}h
                </Text>
            </View>
            {value === "0" ? (
                <ListItem
                    labels={[
                        {
                            text: i18n.t("overtimePaid.add_title"),
                            mode: disabled ? undefined : "cta",
                        },
                    ]}
                    rightIcon="add"
                    rightActionMode="medium"
                    rightIconColor={colors.secondary}
                    style={{ marginHorizontal: -marginSize }}
                    onPress={() => {
                        if (!disabled) {
                            setModalVisible(true);
                        }
                    }}
                />
            ) : (
                <ListItem
                    description={`${i18n.t("overtimePaid.pay_hours", {
                        count: Number(value),
                        hours: value,
                        amount: formatCurrency({
                            amountInCents: hourlyInvoiceAmount * Math.abs(Number(value)),
                            displayDigitsWhenNoCents: false,
                        }),
                    })}`}
                    labels={[
                        {
                            text: i18n.t("payroll_breakdown.edit"),
                            mode: disabled ? undefined : "miniCtaBold",
                            onPress: () => {
                                setModalVisible(true);
                            },
                        },
                    ]}
                    maxDescriptionLines={2}
                    onDelete={onClear}
                    badge={
                        <IconButton
                            onPress={onActionRemove}
                            icon="trash"
                            size={20}
                            iconColor={colors.primary}
                            style={{ marginRight: marginSize / 2 }}
                        />
                    }
                    style={{ backgroundColor: colors.background }}
                    swipeableStyle={{
                        marginHorizontal: -marginSize,
                        borderRadius: 0,
                    }}
                />
            )}
            <OvertimePaidCreateModal
                hourlyInvoiceAmount={hourlyInvoiceAmount}
                formattedEndOfMonthOvertimeTotal={formattedEndOfMonthOvertimeTotal}
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                onCreate={(a) => {
                    setModalVisible(false);
                    onCreate(a);
                }}
            />
        </VStack>
    );
};

const useStyles = () => {
    const { colors, fonts } = useTheme();
    return StyleSheet.create({
        overtimeHours: {
            ...fonts.bold,
            color: colors.secondary,
            fontSize: 14,
        },
        titleContainer: {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
        },
    });
};

export default OvertimePaid;
